import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReviewTabs from './ReviewTabs';

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

function ToggleSwitch({ enabled, onChange }) {
  return (
    <button onClick={onChange} style={{
      width: 40, height: 22, borderRadius: 999, padding: 2,
      background: enabled ? 'var(--accent)' : 'var(--border)', border: 'none',
      cursor: 'pointer', transition: 'background 200ms', display: 'flex', alignItems: 'center',
    }}>
      <span style={{
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transform: enabled ? 'translateX(18px)' : 'translateX(0)', transition: 'transform 200ms', display: 'block',
      }} />
    </button>
  );
}

const money = v => `$${Number(v || 0).toFixed(4)}`;

// Budget banner shared by the wizard steps.
function BudgetBanner({ preview }) {
  if (preview.daily_cost_cap_usd == null) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
        Spent today: <b>{money(preview.today_spend_usd)}</b> · No daily cost cap set.
      </div>
    );
  }
  const over = preview.over_cap;
  return (
    <div style={{
      fontSize: 12, marginBottom: 14, padding: '8px 12px', borderRadius: 'var(--radius)',
      background: over ? 'var(--danger)18' : 'var(--bg-elevated)',
      color: over ? 'var(--danger)' : 'var(--text-secondary)',
      border: over ? '1px solid var(--danger)55' : '1px solid var(--border)',
    }}>
      {over
        ? <>Daily cost cap reached — spent <b>{money(preview.today_spend_usd)}</b> of <b>{money(preview.daily_cost_cap_usd)}</b>. Reviews are paused until tomorrow.</>
        : <>Spent today <b>{money(preview.today_spend_usd)}</b> of <b>{money(preview.daily_cost_cap_usd)}</b> cap · <b>{money(preview.today_remaining_usd)}</b> remaining.</>}
    </div>
  );
}

const dateOnly = v => (v ? String(v).slice(0, 10) : '');

// Format an ISO/timestamp value as a `datetime-local` input value (local tz),
// e.g. "2026-07-01T09:30". Returns '' for empty/unparseable input.
function toLocalInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Human-friendly display of a `datetime-local` value ("2026-07-01T09:30" → locale string).
function fmtLocalInput(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

// True if `updated` (ISO) falls within [from, to] (inclusive). `from`/`to` are
// `datetime-local` strings (or empty). Unknown/unparseable dates are kept —
// matches the backend's fail-open behaviour.
function inDateRange(updated, from, to) {
  if (!updated) return true;
  const t = new Date(updated).getTime();
  if (Number.isNaN(t)) return true;
  if (from) { const f = new Date(from).getTime(); if (!Number.isNaN(f) && t < f) return false; }
  if (to)   { const e = new Date(to).getTime();   if (!Number.isNaN(e) && t > e) return false; }
  return true;
}

// "Review now" wizard: Step 1 pick the PRs (date-filtered), Step 2 confirm & launch.
function RunNowModal({ config, onClose, onRan }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);   // 0 = choose window, 1 = pick PRs, 2 = confirm
  const [selected, setSelected] = useState(() => new Set());
  const [running, setRunning] = useState(false);
  const [q, setQ] = useState('');
  // Default the "since" to the config's last run — same window scheduled runs
  // use — so ancient never-merged PRs aren't pulled in. Editable before search.
  const [fromDate, setFromDate] = useState(() => toLocalInput(config.last_run?.started_at));
  const [toDate, setToDate] = useState('');

  // Search (preview) runs on demand from step 0, once the window is chosen —
  // not on mount — so a large multi-repo config isn't searched before the user
  // has picked how far back to look.
  function runSearch() {
    setLoading(true);
    setError('');
    axios.post(`/code-review/configs/${config.id}/preview`)
      .then(res => {
        setPreview(res.data);
        // Default-select fresh PRs (not already reviewed) inside the chosen window.
        const seed = (res.data.sample || [])
          .filter(p => !p.already_reviewed && inDateRange(p.updated_at, fromDate, toDate))
          .map(p => p.pr_url);
        setSelected(new Set(seed));
        setStep(1);
      })
      .catch(e => setError(e.response?.data?.error || 'Failed to preview PRs'))
      .finally(() => setLoading(false));
  }

  const prs = preview?.sample || [];
  const inWin = pr => inDateRange(pr.updated_at, fromDate, toDate);
  // Visible list = date window + text query. Selection only counts within the window.
  const filtered = prs.filter(pr => inWin(pr) &&
    (!q.trim() || `${pr.repo} #${pr.pr_id} ${pr.title} ${pr.author}`.toLowerCase().includes(q.trim().toLowerCase())));
  const effective = prs.filter(pr => selected.has(pr.pr_url) && inWin(pr));
  const selCount = effective.length;

  function toggle(url) {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(url)) n.delete(url); else n.add(url);
      return n;
    });
  }
  const allShownSelected = filtered.length > 0 && filtered.every(p => selected.has(p.pr_url));
  function toggleAllShown() {
    setSelected(prev => {
      const n = new Set(prev);
      if (allShownSelected) filtered.forEach(p => n.delete(p.pr_url));
      else filtered.forEach(p => n.add(p.pr_url));
      return n;
    });
  }

  async function run() {
    setRunning(true);
    try {
      // Only PRs both selected AND inside the date window are sent. max_prs =
      // count so nothing is truncated; the daily cost cap still hard-stops the run.
      // force: hand-picking a PR here means "review this one" — including the ones
      // flagged as already reviewed, which stay selectable on purpose.
      const urls = effective.map(p => p.pr_url);
      await axios.post(`/code-review/configs/${config.id}/run`,
        { pr_urls: urls, max_prs: urls.length, force: true,
          date_from: fromDate || undefined, date_to: toDate || undefined });
      onRan();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start review');
      setRunning(false);
    }
  }

  const overCap = preview?.over_cap;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div className="card" style={{ width: 660, maxWidth: '92vw', padding: 24, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Review now — {config.name}</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Step {step + 1} of 3 — {step === 0 ? 'Choose how far back to search'
            : step === 1 ? 'Select the pull requests to review' : 'Confirm & start'}
        </div>

        {loading ? <div className="spinner" /> : (
          <>
            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

            {step === 0 && (
              <>
                <p style={{ fontSize: 14, marginTop: 0 }}>
                  Search for open pull requests updated <b>since</b> a given date &amp; time.
                  {config.repo_scope === 'all'
                    ? ` This config covers every ${config.scope_provider || ''} repository`
                      + `${config.scope_owner ? ` under ${config.scope_owner}` : ''} that currently has an open PR;`
                    : ` This config spans ${config.repositories?.length || 0} `
                      + `repositor${(config.repositories?.length || 0) === 1 ? 'y' : 'ies'};`}
                  narrowing the window keeps the search fast and focused.
                </p>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 8, padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }}>
                  <div>
                    <label className="form-label" style={{ marginBottom: 2 }}>Updated since</label>
                    <input type="datetime-local" className="form-control" style={{ width: 220 }}
                      value={fromDate} onChange={e => setFromDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label" style={{ marginBottom: 2 }}>Until (optional)</label>
                    <input type="datetime-local" className="form-control" style={{ width: 220 }}
                      value={toDate} onChange={e => setToDate(e.target.value)} />
                  </div>
                </div>
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  {config.last_run
                    ? <>Defaults to your last run ({fmtLocalInput(toLocalInput(config.last_run.started_at))}). Clear &quot;since&quot; to search all open PRs.</>
                    : <>No prior run — leave &quot;since&quot; empty to search all open PRs, or set it to narrow the window.</>}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                  <button className="btn btn-primary" onClick={runSearch}>Search PRs →</button>
                </div>
              </>
            )}

            {step > 0 && preview && (
              <>
                <BudgetBanner preview={preview} />

            {step === 1 && (
              <>
                {preview.reviewable_prs === 0 ? (
                  <div className="empty-state" style={{ padding: 24 }}>
                    <p>No open PRs match this config's target branches right now.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 8, padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }}>
                      <div>
                        <label className="form-label" style={{ marginBottom: 2 }}>Updated since</label>
                        <input type="datetime-local" className="form-control" style={{ width: 200 }}
                          value={fromDate} onChange={e => setFromDate(e.target.value)} />
                      </div>
                      <div>
                        <label className="form-label" style={{ marginBottom: 2 }}>Until</label>
                        <input type="datetime-local" className="form-control" style={{ width: 200 }}
                          value={toDate} onChange={e => setToDate(e.target.value)} />
                      </div>
                      <button className="btn btn-ghost" style={{ fontSize: 12 }}
                        title="Reset to the last review date"
                        onClick={() => { setFromDate(toLocalInput(preview.last_review_at)); setToDate(''); }}>
                        Reset
                      </button>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1, minWidth: 160 }}>
                        Refine the window to filter these results — no re-search needed.
                        {preview.last_review_at && <> Last review: {dateOnly(preview.last_review_at)}.</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input className="form-control" placeholder="Filter by repo, #id, title, author…"
                        value={q} onChange={e => setQ(e.target.value)} style={{ flex: 1 }} />
                      <button className="btn btn-secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={toggleAllShown}>
                        {allShownSelected ? 'Deselect shown' : 'Select shown'}
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {selCount} selected · {filtered.length} in range · {preview.reviewable_prs} open
                      {preview.excluded_by_title > 0 && <> · {preview.excluded_by_title} excluded by title filter</>}
                      {preview.sample_truncated && <> · showing first {prs.length}</>}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 12, minHeight: 120 }}>
                      {filtered.map(pr => (
                        <label key={pr.pr_url} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={selected.has(pr.pr_url)} onChange={() => toggle(pr.pr_url)} style={{ marginTop: 3 }} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 13 }}>
                              <span style={{ color: 'var(--text-muted)' }}>{pr.repo} #{pr.pr_id}</span> {pr.title || '(no title)'}
                            </span>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              <span>{pr.author || 'unknown'} → {pr.target_branch}</span>
                              {pr.updated_at && <span>updated {dateOnly(pr.updated_at)}</span>}
                              {pr.is_draft && <span className="badge" style={{ background: 'var(--warning)22', color: 'var(--warning)' }}>draft</span>}
                              {pr.already_reviewed && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>already reviewed</span>}
                            </div>
                          </span>
                        </label>
                      ))}
                      {filtered.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>No PRs updated in this date range.</div>}
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setStep(0)}>← Change window</button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={() => setStep(2)} disabled={selCount === 0}>
                      Next — {selCount} PR(s)
                    </button>
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p style={{ fontSize: 14 }}>
                  About to review <b>{selCount}</b> selected pull request{selCount === 1 ? '' : 's'}
                  {fromDate && <> updated since <b>{fmtLocalInput(fromDate)}</b></>}{toDate && <> through <b>{fmtLocalInput(toDate)}</b></>}.
                </p>
                <p className="form-hint" style={{ marginBottom: 12 }}>
                  {config.post_mode === 'dry_run'
                    ? 'Dry-run: findings are saved to the dashboard, nothing is posted to the PR.'
                    : `Post mode: ${config.post_mode} — comments will be posted to each PR.`}
                  {' '}No PR-count limit — the run stops only when the daily cost cap is reached.
                </p>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 8, marginBottom: 12 }}>
                  {effective.map(pr => (
                    <div key={pr.pr_url} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{pr.repo} #{pr.pr_id}</span> {pr.title || '(no title)'}
                    </div>
                  ))}
                </div>
                {overCap && <div className="error-msg" style={{ marginBottom: 12 }}>Daily cost cap reached — reviews are paused. Raise the cap in Cost &amp; Usage to proceed.</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={run} disabled={running || selCount === 0 || overCap}>
                      {running ? 'Starting…' : `Review ${selCount} PR(s)`}
                    </button>
                  </div>
                </div>
              </>
            )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ReviewConfigsList() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [runNow, setRunNow] = useState(null);
  const [toast, setToast] = useState('');

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/code-review/configs');
      setConfigs(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  async function toggle(c) {
    try {
      await axios.put(`/code-review/configs/${c.id}`, { enabled: !c.enabled });
      setConfigs(prev => prev.map(x => x.id === c.id ? { ...x, enabled: !x.enabled } : x));
    } catch (e) { alert(e.response?.data?.error || 'Failed to update'); }
  }

  async function remove(c) {
    if (!window.confirm(`Delete config "${c.name}"? This removes its schedule too.`)) return;
    try { await axios.delete(`/code-review/configs/${c.id}`); fetchConfigs(); }
    catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  }

  return (
    <div>
      <div className="page-header">
        <h2>AI Code Review</h2>
        <p>Configure which repositories and branches get AI-reviewed, and how often.</p>
      </div>
      <div className="page-body">
        <ReviewTabs />
        <div className="toolbar">
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => navigate('/code-review/configs/new')}>+ New config</button>
        </div>

        {error && <div className="error-msg">{error}</div>}
        {toast && <div className="alert alert-success" style={{ marginBottom: 12 }}>{toast}</div>}

        {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
          : configs.length === 0 ? (
            <div className="empty-state">
              <h3>No review configs yet</h3>
              <p>Create one to start scanning a repository's pull requests.</p>
              <button className="btn btn-primary" onClick={() => navigate('/code-review/configs/new')}>+ New config</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {configs.map(c => (
                <div key={c.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 16 }}>{c.name}</span>
                        <span className="badge" title={(c.repositories || []).map(r => `${r.provider}:${r.repo_name}`).join('\n')}
                          style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                          {c.repo_scope === 'all'
                            ? `${c.scope_provider || 'provider'}: every repo with an open PR`
                              + (c.scope_owner ? ` under ${c.scope_owner}` : '')
                            : (c.repositories && c.repositories.length)
                              ? (c.repositories.length === 1
                                  ? `${c.repositories[0].provider}:${c.repositories[0].repo_name}`
                                  : `${c.repositories.length} repositories`)
                              : (c.repo_name ? `${c.provider}:${c.repo_name}` : 'no repos')}
                        </span>
                        <span className="badge" style={{ background: c.post_mode === 'dry_run' ? 'var(--warning)22' : 'var(--success)22', color: c.post_mode === 'dry_run' ? 'var(--warning)' : 'var(--success)' }}>{c.post_mode}</span>
                      </div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 6 }}>
                        Branches: {(c.target_branches || []).join(', ') || '—'} · Model: {c.llm_model} · Max {c.max_prs_per_run} PRs/run
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.enabled ? 'Enabled' : 'Disabled'}</span>
                      <ToggleSwitch enabled={!!c.enabled} onChange={() => toggle(c)} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 14 }}>
                    {[
                      { label: 'Next run', value: fmtDate(c.next_run_at) },
                      { label: 'Last run', value: c.last_run ? fmtDate(c.last_run.started_at) : '—' },
                      { label: 'Last findings', value: c.last_run?.findings_count ?? '—' },
                      { label: 'Last cost', value: c.last_run ? `$${Number(c.last_run.cost_usd).toFixed(4)}` : '—' },
                    ].map(s => (
                      <div key={s.label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setRunNow(c)}>Review now…</button>
                    {c.last_run && <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => navigate(`/code-review/runs?config_id=${c.id}`)}>View runs</button>}
                    <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => navigate(`/code-review/configs/${c.id}/edit`)}>Edit</button>
                    <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--danger)' }} onClick={() => remove(c)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {runNow && <RunNowModal config={runNow} onClose={() => setRunNow(null)}
        onRan={() => { setRunNow(null); setToast('Review started — check Runs in a moment.'); setTimeout(fetchConfigs, 1500); }} />}
    </div>
  );
}
